import { StatePage } from "@/components/state-page";
import { filterKeys } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { requirePermissions } from "@/utils/permissions";
import { Select } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { createFileRoute } from "@tanstack/react-router";
import { AdminAnalytics } from "@workspace/admin-domain/src/features/analytics/schema";
import dayjs from "@workspace/domain/src/utils/dayjs";
import { t } from "i18next";
import { LucideCalendar } from "lucide-react";
import { useState } from "react";
import { ClientsSection } from "./-clients";
import { OverviewSection } from "./-overview";
import { PaymentsSection } from "./-payments";
import { ReservationsSection } from "./-reservations";
import { TripsSection } from "./-trips";

type TDateControlValue = "thisMonth" | "lastThreeMonths" | "thisYear" | "allTime" | "custom";

export const Route = createFileRoute("/_authenticated/analytics/")({
  component: Component,
  validateSearch: AdminAnalytics.DateRange,
  beforeLoad: requirePermissions("analytics:get"),
  loaderDeps: ({ search }) => filterKeys(search, AdminAnalytics.DateRange.shape),
  loader: async ({ context, deps }) => {
    const { startDate, endDate } = deps;
    void Promise.all([
      // Overview
      context.queryClient.prefetchQuery(
        orpc.analytics.overview.getTotalReservations.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.finance.getTotalRevenue.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.overview.getTotalDue.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.overview.getNewClients.queryOptions({
          input: { startDate, endDate },
        }),
      ),

      // Reservations
      context.queryClient.prefetchQuery(
        orpc.analytics.reservations.getOverTime.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.reservations.getRevenueOverTime.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.reservations.getStatusDistribution.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.reservations.getCancellationMetrics.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.reservations.getAverageRevenue.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.reservations.getComposition.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.reservations.getAverageBookingLeadTime.queryOptions({
          input: { startDate, endDate },
        }),
      ),

      // Trips
      context.queryClient.prefetchQuery(
        orpc.analytics.trips.getTopByRevenue.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.trips.getTopByReservations.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.trips.getStartingSoon.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.trips.getLowCapacity.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.trips.getAudienceDistribution.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.trips.getFoodDistribution.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.trips.getTransportationDistribution.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.trips.getAccessibilityDistribution.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.trips.getGuidesDistribution.queryOptions({
          input: { startDate, endDate },
        }),
      ),

      // Clients
      context.queryClient.prefetchQuery(
        orpc.analytics.clients.getSegmentation.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.clients.getNewVsReturning.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.clients.getAcquisitionsOverTime.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.clients.getTopBySpend.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.clients.getOutstandingDues.queryOptions({
          input: { startDate, endDate },
        }),
      ),

      // Payments
      context.queryClient.prefetchQuery(
        orpc.analytics.payments.getCompletionRate.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.payments.getTotalPaid.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.payments.getTotalDue.queryOptions({
          input: { startDate, endDate },
        }),
      ),
      context.queryClient.prefetchQuery(
        orpc.analytics.payments.getTotalRefunded.queryOptions({
          input: { startDate, endDate },
        }),
      ),
    ]);
  },
  errorComponent: ({ error, reset }) => (
    <StatePage error={error} reset={reset} resourceType="analytics" emptyResourceType={undefined} />
  ),
});

function Component() {
  const { startDate, endDate } = Route.useSearch();

  return (
    <div className="flex grow flex-col gap-md md:gap-lg">
      <AnalyticsHeader />

      <OverviewSection startDate={startDate} endDate={endDate} />
      <ReservationsSection startDate={startDate} endDate={endDate} />
      <TripsSection startDate={startDate} endDate={endDate} />
      <ClientsSection startDate={startDate} endDate={endDate} />
      <PaymentsSection startDate={startDate} endDate={endDate} />
    </div>
  );
}

const DEFAULT_DATE_RANGES = {
  thisMonth: {
    startDate: dayjs().subtract(30, "day").startOf("day").toDate(),
    endDate: dayjs().endOf("day").toDate(),
  },
  lastThreeMonths: {
    startDate: dayjs().subtract(90, "day").startOf("day").toDate(),
    endDate: dayjs().endOf("day").toDate(),
  },
  thisYear: {
    startDate: dayjs().subtract(1, "year").startOf("day").toDate(),
    endDate: dayjs().endOf("day").toDate(),
  },
  allTime: {
    startDate: dayjs("1970-01-01").toDate(),
    endDate: dayjs().endOf("day").toDate(),
  },
} as const;

function rangeToDateControlValue(startDate: Date, endDate: Date) {
  if (
    startDate?.getTime() === DEFAULT_DATE_RANGES.thisMonth.startDate?.getTime() &&
    endDate?.getTime() === DEFAULT_DATE_RANGES.thisMonth.endDate?.getTime()
  ) {
    return "thisMonth";
  }
  if (
    startDate?.getTime() === DEFAULT_DATE_RANGES.lastThreeMonths.startDate?.getTime() &&
    endDate?.getTime() === DEFAULT_DATE_RANGES.lastThreeMonths.endDate?.getTime()
  ) {
    return "lastThreeMonths";
  }
  if (
    startDate?.getTime() === DEFAULT_DATE_RANGES.thisYear.startDate?.getTime() &&
    endDate?.getTime() === DEFAULT_DATE_RANGES.thisYear.endDate?.getTime()
  ) {
    return "thisYear";
  }
  if (
    startDate?.getTime() === DEFAULT_DATE_RANGES.allTime.startDate?.getTime() &&
    endDate?.getTime() === DEFAULT_DATE_RANGES.allTime.endDate?.getTime()
  ) {
    return "allTime";
  }
  return "custom";
}

function dateControlValueToRange(dateControlValue: TDateControlValue) {
  switch (dateControlValue) {
    case "thisMonth":
      return {
        startDate: DEFAULT_DATE_RANGES.thisMonth.startDate,
        endDate: DEFAULT_DATE_RANGES.thisMonth.endDate,
      };
    case "lastThreeMonths":
      return {
        startDate: DEFAULT_DATE_RANGES.lastThreeMonths.startDate,
        endDate: DEFAULT_DATE_RANGES.lastThreeMonths.endDate,
      };
    case "thisYear":
      return {
        startDate: DEFAULT_DATE_RANGES.thisYear.startDate,
        endDate: DEFAULT_DATE_RANGES.thisYear.endDate,
      };
    case "allTime":
      return {
        startDate: DEFAULT_DATE_RANGES.allTime.startDate,
        endDate: DEFAULT_DATE_RANGES.allTime.endDate,
      };
    case "custom":
      return {
        startDate: undefined,
        endDate: undefined,
      };
  }
}

function AnalyticsHeader() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const [dateControlValue, setDateControlValue] = useState<TDateControlValue>(
    () => rangeToDateControlValue(search.startDate, search.endDate),
  );

  const [localStartDate, setLocalStartDate] = useState<Date | undefined>(search.startDate);
  const [localEndDate, setLocalEndDate] = useState<Date | undefined>(search.endDate);

  return (
    <div className="flex flex-col gap-md py-md">
      <div className="flex flex-col gap-md md:flex-row md:items-center md:justify-between">
        <p className="text-2xl font-semibold md:text-3xl">{t("analytics.title")}</p>
        <div className="flex flex-col-reverse items-center gap-sm md:flex-row">
          {dateControlValue === "custom" ? (
            <DatePickerInput
              type="range"
              placeholder={t("analytics.dateRange.customRange")}
              value={[localStartDate ?? null, localEndDate ?? null]}
              onChange={(value) => {
                const newStartDate = value[0] ? dayjs(value[0]).toDate() : undefined;
                const newEndDate = value[1] ? dayjs(value[1]).toDate() : undefined;

                setLocalStartDate(newStartDate);
                setLocalEndDate(newEndDate);

                if (!newStartDate && !newEndDate) {
                  setDateControlValue("allTime");
                }

                if (!(newStartDate && !newEndDate)) {
                  void navigate({
                    search: {
                      startDate: newStartDate,
                      endDate: newEndDate,
                    },
                  });
                }
              }}
              leftSection={<LucideCalendar className="size-4" />}
              className="w-full md:w-auto"
              maxDate={dayjs().toDate()}
              clearable
            />
          ) : null}

          <Select
            unselectable="on"
            className="w-full md:w-40"
            value={dateControlValue}
            onChange={(value) => {
              if (!value) return;

              const newValue = value as TDateControlValue;
              setDateControlValue(newValue);

              if (newValue !== "custom") {
                void navigate({ search: dateControlValueToRange(newValue) });
              }
            }}
            data={[
              {
                label: t("analytics.dateRange.thisMonth"),
                value: "thisMonth",
              },
              {
                label: t("analytics.dateRange.lastThreeMonths"),
                value: "lastThreeMonths",
              },
              {
                label: t("analytics.dateRange.thisYear"),
                value: "thisYear",
              },
              {
                label: t("analytics.dateRange.allTime"),
                value: "allTime",
              },
              {
                label: t("analytics.dateRange.custom"),
                value: "custom",
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
